import { msg } from "../locales/translate";
import type { IPaymentSession } from "../types/types";

export const BATCH_EXPORT_USER_IDS = [2, 22] as const;
export const BATCH_REQUEST_INTERVAL_MS = 100;
export const BATCH_DEFAULT_MAX_TRANSACTIONS = 1000;
export const BATCH_MAX_BYTES = 256 * 1024 * 1024;
export const BATCH_PART_BYTES = 128 * 1024 * 1024;
const MAX_PAGES = 10000;
export const BATCH_STAGES = ["list", "transactions", "processing", "logs", "archive"] as const;
export type BatchStage = typeof BATCH_STAGES[number];
export interface BatchProgress {
	stage: BatchStage;
	completed: number;
	total: number | null;
	message: string;
	requests: number;
	estimatedRemainingMs?: number | null;
	retryAt?: number;
	retryAttempt?: number;
}
type RecordData = Record<string, unknown>;
type ItemState = "pending" | "complete" | "skipped";
export interface BatchItem {
	id: string;
	transaction: ItemState;
	processing: ItemState;
	log: ItemState;
	processingLog: ItemState;
	processingId?: string;
	processingSessionId?: string;
	reason?: string;
	excluded?: { source: "list" | "details"; fail_reason: string; fail_code: unknown };
}
export interface BatchResult {
	files: Record<string, Uint8Array>;
	manifest: {
		version: number; status: "complete" | "cancelled" | "failed";
		projectId: number; userId: number; filters: RecordData;
		startedAt: string; finishedAt: string; intervalMs: number; requests: number;
		listComplete: boolean; listed: number; expectedTotal: number | null;
		excludeInsufficientFunds: boolean; excluded: number;
		maxTransactions: number; limitReached: boolean;
		networkRetries: number;
		exportId: string;
		parts: { number: number; files: string[]; final: boolean }[];
		error?: string; stoppedAt?: { stage: BatchStage; message: string };
		items: BatchItem[];
	};
}
export interface BatchOptions {
	projectId: number;
	userId: number;
	filters: RecordData;
	excludeInsufficientFunds?: boolean;
	maxTransactions?: number;
	signal: AbortSignal;
	request: (request: IAPIRequest) => Promise<IAPIResponse>;
	checkContext: () => Promise<void>;
	onProgress: (progress: BatchProgress) => void;
	/** Must finish storing/compressing this part before collection can continue. */
	onPart?: (files: Record<string, Uint8Array>, exportId: string, partNumber: number) => Promise<void>;
	/** Smaller part sizes may be injected for tests. */
	partBytes?: number;
	/** Dependency injection for deterministic scheduling tests. */
	wait?: (ms: number, signal: AbortSignal) => Promise<void>;
}
let running = false;
const encoder = new TextEncoder();
const record = (value: unknown): RecordData | null => value !== null && typeof value === "object" && !Array.isArray(value) ? value as RecordData : null;
const message = (error: unknown) => error instanceof Error ? error.message : String(error ?? msg("unknownError"));
const identifier = (value: unknown) => {
	const id = String(value ?? "");
	if (!/^[a-zA-Z0-9_-]+$/.test(id) || id === "0") throw new Error(msg("aResponseContainsAnInvalidTransactionSessionId"));
	return id;
};
const integer = (value: unknown): number | null => {
	if (value === "" || value == null) return null;
	const number = Number(value);
	return Number.isSafeInteger(number) && number >= 0 ? number : null;
};

export function waitBetweenBatchRequests(ms: number, signal: AbortSignal): Promise<void> {
	signal.throwIfAborted();
	return new Promise((resolve, reject) => {
		const abort = () => { clearTimeout(timer); reject(signal.reason); };
		const timer = setTimeout(() => { signal.removeEventListener("abort", abort); resolve(); }, ms);
		signal.addEventListener("abort", abort, { once: true });
	});
}

function pageData(data: unknown) {
	const object = record(data);
	const items = Array.isArray(data) ? data : object?.items ?? object?.rows;
	if (!Array.isArray(items)) throw new Error(msg("theServerReturnedAnInvalidListPage"));
	const pagination = record(object?.pagination) ?? record(object?.pages) ?? object;
	return {
		items,
		total: integer(pagination?.total),
		totalPages: integer(pagination?.totalPages),
		page: integer(pagination?.page ?? pagination?.current),
	};
}

function csvValue(value: unknown) {
	let text = value == null ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
	// Opening downloaded data in a spreadsheet must not execute a formula.
	if (/^[\s]*[=+\-@]/.test(text)) text = "'" + text;
	return `"${text.replace(/"/g, '""')}"`;
}
export function batchRowsCsv(rows: RecordData[]) {
	const keys = Array.from(new Set(rows.flatMap(row => Object.keys(row))));
	const priority = ["id", "created_at", "status", "type", "amount", "currency", "processing_id"];
	const headers = [...priority.filter(key => keys.includes(key)), ...keys.filter(key => !priority.includes(key))];
	return "\uFEFF" + [headers.map(csvValue).join(","), ...rows.map(row => headers.map(key => csvValue(row[key])).join(","))].join("\r\n");
}

/**
 * Four sequential network phases. Every call, including log pages, shares the
 * same scheduler. Only network failures retry. No calls to AI services.
 */
export async function collectTransactionBatch(options: BatchOptions): Promise<BatchResult> {
	if (!(BATCH_EXPORT_USER_IDS as readonly number[]).includes(options.userId)) throw new Error(msg("batchAnalysisIsNotAvailableForThisUser"));
	const maxTransactions = options.maxTransactions ?? BATCH_DEFAULT_MAX_TRANSACTIONS;
	if (!Number.isSafeInteger(maxTransactions) || maxTransactions < 1) throw new Error(msg("maximumTransactionsMustBeAPositiveWholeNumber"));
	const partBytes = options.partBytes ?? BATCH_PART_BYTES;
	if (!Number.isSafeInteger(partBytes) || partBytes < 1 || partBytes > BATCH_MAX_BYTES) throw new Error(msg("invalidArchivePartSize"));
	if (running) throw new Error(msg("anotherBatchCollectionIsAlreadyRunningStopItBeforeStartingANewOne"));
	running = true;
	const { signal } = options;
	let files: Record<string, Uint8Array> = {};
	const rows: RecordData[] = [];
	let bytes = 0;
	let listBytes = 0;
	let fileCount = 0;
	let requestDurationMs = 0;
	let completedRequests = 0;
	let logPageRequests = 0;
	let completedDectaLogs = 0;
	let currentLogPages = 0;
	let currentLogTotalPages: number | null = null;
	let estimateAt = 0;
	let estimatedRemainingMs: number | null = null;
	let progress: BatchProgress = { stage: "list", completed: 0, total: null, message: msg("loadingTheFilteredTransactionList"), requests: 0 };
	const manifest: BatchResult["manifest"] = {
		version: 1, status: "complete", projectId: options.projectId, userId: options.userId,
		filters: structuredClone(options.filters), startedAt: new Date().toISOString(), finishedAt: "",
		intervalMs: BATCH_REQUEST_INTERVAL_MS, requests: 0, listComplete: false, listed: 0, expectedTotal: null, items: [],
		excludeInsufficientFunds: options.excludeInsufficientFunds ?? true, excluded: 0,
		maxTransactions, limitReached: false,
		networkRetries: 0,
		exportId: `transaction-analysis-${options.projectId}-${new Date().toISOString().replace(/[:.]/g, "-")}`,
		parts: [],
	};
	const exclude = (item: BatchItem, data: RecordData, source: "list" | "details") => {
		if (!manifest.excludeInsufficientFunds || data.status !== "failed" || typeof data.fail_reason !== "string"
			|| data.fail_reason.trim().toLowerCase().replace(/\s+/g, " ") !== "decline, not sufficient funds / over credit limit") return false;
		item.excluded = { source, fail_reason: data.fail_reason, fail_code: data.fail_code ?? null };
		item.transaction = item.processing = item.log = item.processingLog = "skipped";
		item.reason = "Excluded: insufficient funds / over credit limit.";
		manifest.excluded++;
		return true;
	};
	const report = (stage: BatchStage, completed: number, total: number | null, text: string) => {
		// Throttle the scan for large selections; estimates include requests still
		// needed in later stages, not just the currently visible progress bar.
		const now = Date.now();
		if (now - estimateAt >= 1000 || stage !== progress.stage) {
			estimateAt = now;
			let remaining = 0;
			const pagesPerLog = completedDectaLogs ? Math.max(1, (logPageRequests - currentLogPages) / completedDectaLogs) : 1;
			for (const item of manifest.items) {
				if (item.excluded || item.transaction === "skipped") continue;
				if (item.transaction === "pending") remaining += 4; // Until details identify the integration, assume Decta.
				else {
					if (item.processing === "pending") remaining++;
					if (item.log === "pending") remaining++;
					if (item.processingLog === "pending") remaining += pagesPerLog;
				}
			}
			if (stage === "list") {
				if (manifest.expectedTotal === null) remaining = NaN;
				else {
					const unseen = Math.max(0, Math.min(manifest.expectedTotal, maxTransactions) - rows.length);
					remaining += Math.ceil(unseen / 20) + unseen * (rows.length ? 1 - manifest.excluded / rows.length : 1) * 4;
				}
			}
			if (stage === "logs" && currentLogPages) {
				remaining = Math.max(0, remaining - pagesPerLog)
					+ Math.max(1, (currentLogTotalPages ?? pagesPerLog) - currentLogPages);
			}
			estimatedRemainingMs = completedRequests >= 3 && Number.isFinite(remaining)
				? remaining * (requestDurationMs / completedRequests + BATCH_REQUEST_INTERVAL_MS) : null;
		}
		progress = { stage, completed, total, message: text, requests: manifest.requests, estimatedRemainingMs };
		options.onProgress(progress);
	};
	const partInfo = (final: boolean) => ({
		exportId: manifest.exportId, number: manifest.parts.length + 1, final,
		totalParts: final ? manifest.parts.length + 1 : null,
		files: Object.keys(files),
		instructions: "Download every numbered ZIP with this exportId. Extract all parts into one folder. The final part contains the full table and manifest.json, including the file index for all parts. A transaction's sources may be in different parts. Earlier parts alone are not a complete export.",
	});
	const put = async (path: string, data: unknown) => {
		const content = encoder.encode(JSON.stringify(data, null, 2));
		if (content.byteLength > BATCH_MAX_BYTES) throw new Error(msg("aSingleJsonFileExceeds256MbAndCannotBeExportedInThisBrowser"));
		if (fileCount && (bytes + content.byteLength > partBytes || fileCount >= 59000)) {
			if (!options.onPart) throw new Error(msg("archivePartIsFullAPartHandlerIsRequiredToContinue"));
			const info = partInfo(false);
			files[`parts/part-${String(info.number).padStart(3, "0")}.json`] = encoder.encode(JSON.stringify(info, null, 2));
			options.onProgress({ ...progress, estimatedRemainingMs: null, message: msg("templatePackingArchivePartCollectionWillContinueAutomatically_6eebf6e7", { v0: info.number }) });
			await options.onPart(files, manifest.exportId, info.number);
			manifest.parts.push({ number: info.number, files: info.files, final: false });
			files = {}; bytes = 0; fileCount = 0;
			await options.checkContext();
		}
		bytes += content.byteLength - (files[path]?.byteLength ?? 0);
		if (!files[path]) fileCount++;
		files[path] = content;
	};
	const read = async (method: string, data: object) => {
		let retryAttempt = 0;
		for (;;) {
		signal.throwIfAborted();
		if (manifest.requests && !retryAttempt) await (options.wait ?? waitBetweenBatchRequests)(BATCH_REQUEST_INTERVAL_MS, signal);
		signal.throwIfAborted();
		await options.checkContext();
		signal.throwIfAborted();
		manifest.requests++;
		options.onProgress({ ...progress, requests: manifest.requests });
		const requestStartedAt = Date.now();
		let response: IAPIResponse;
		try {
		response = await options.request({
			method, data, signal, expectedProjectId: options.projectId,
			skipSessionRecovery: true, timeoutMs: 60000, headers: { ignoreBusy: "true" },
		});
		} catch (error) {
			signal.throwIfAborted();
			const value = error as { code?: string; response?: unknown };
			if (!value?.response && ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(value?.code ?? "")) {
				response = { error: { code: -1, message: message(error), networkError: true } };
			} else throw error;
		}
		signal.throwIfAborted();
		await options.checkContext();
		if (response.error?.networkError && !response.error.httpStatus) {
			retryAttempt++;
			manifest.networkRetries++;
			const delay = Math.min(30000, 5000 * 2 ** Math.min(retryAttempt - 1, 3));
			options.onProgress({ ...progress, requests: manifest.requests, estimatedRemainingMs: null,
				retryAttempt, retryAt: Date.now() + delay, message: msg("connectionInterruptedCollectedDataIsKeptTheCurrentRequestWillRetr") });
			await (options.wait ?? waitBetweenBatchRequests)(delay, signal);
			continue;
		}
		if (response.error) throw new Error(`${method}: ${response.error.message || "Request failed"}`);
		if (response.data == null) throw new Error(msg("templateTheServerReturnedNoData_6e1566cf", { v0: method }));
		requestDurationMs += Date.now() - requestStartedAt;
		completedRequests++;
		return response.data as unknown;
		}
	};
	try {
		const seenIds = new Set<string>();
		let expectedPages: number | null = null;
		for (let page = 0; ; page++) {
			if (page >= MAX_PAGES) throw new Error(msg("listPaginationExceededTheSafetyLimit"));
			report("list", rows.length, manifest.expectedTotal === null ? maxTransactions : Math.min(manifest.expectedTotal, maxTransactions), msg("templateLoadingListPageTransactionsCollected_bfdbd864", { v0: page + 1, v1: rows.length }));
			const result = pageData(await read("session/list", { ...manifest.filters, pagination: { page, perPage: 20 } }));
			if (result.page !== null && result.page !== page) throw new Error(msg("theServerReturnedAnUnexpectedListPage"));
			if (page === 0) { manifest.expectedTotal = result.total; expectedPages = result.totalPages; }
			else if ((manifest.expectedTotal !== null && result.total !== null && result.total !== manifest.expectedTotal)
				|| (expectedPages !== null && result.totalPages !== null && result.totalPages !== expectedPages)) {
				throw new Error(msg("theFilteredListChangedDuringCollectionThisArchiveIsIncompleteRunA"));
			}
			for (const raw of result.items) {
				if (rows.length >= maxTransactions) break;
				const row = record(raw);
				if (!row) throw new Error(msg("invalidTransactionInTheList"));
				const id = identifier(row.id);
				if (seenIds.has(id)) throw new Error(msg("aTransactionWasRepeatedAcrossListPagesCollectionStoppedToAvoidAnI"));
				// Account for the in-memory list as well as exported detail files.
				const size = encoder.encode(JSON.stringify(row)).byteLength;
				if (listBytes + size > BATCH_MAX_BYTES / 3) throw new Error(msg("theListIsTooLargeForABrowserExportUseANarrowerFilter"));
				listBytes += size;
				seenIds.add(id); rows.push(row);
				const item: BatchItem = { id, transaction: "pending", processing: "pending", log: "pending", processingLog: "pending" };
				manifest.items.push(item);
				exclude(item, row, "list");
			}
			manifest.listed = rows.length;
			if (rows.length >= maxTransactions) {
				manifest.limitReached = true;
				manifest.listComplete = true;
				break;
			}
			const finished = expectedPages !== null ? page + 1 >= expectedPages
				: manifest.expectedTotal !== null ? rows.length >= manifest.expectedTotal : result.items.length === 0;
			if (finished) {
				if (manifest.expectedTotal !== null && rows.length !== manifest.expectedTotal) throw new Error(msg("theTransactionListIsIncomplete"));
				manifest.listComplete = true; break;
			}
			if (!result.items.length) throw new Error(msg("aTransactionListPageIsMissing"));
		}
		report("list", rows.length, rows.length, msg("templateCollectedTransactions_7eafaa6c", { v0: rows.length }));

		for (let index = 0; index < manifest.items.length; index++) {
			const item = manifest.items[index];
			report("transactions", index, rows.length, msg("templateTransactionOf_f6b2bfd4", { v0: item.id, v1: index + 1, v2: rows.length }));
			if (item.excluded) continue;
			if (rows[index]._is_release) {
				item.transaction = item.processing = item.log = item.processingLog = "skipped";
				item.reason = "Synthetic rolling-release row, not a payment transaction.";
				continue;
			}
			const transaction = record(await read("session/get", { id: rows[index].id }));
			if (!transaction || String(transaction.id) !== item.id) throw new Error(msg("templateTransactionResponseIdDoesNotMatch_6aa38f24", { v0: item.id }));
			if (transaction.project_id != null && Number(transaction.project_id) !== options.projectId) throw new Error(msg("transactionBelongsToADifferentProject"));
			if (exclude(item, transaction, "details")) continue;
			await put(`transactions/${item.id}.json`, transaction);
			item.transaction = "complete";
			const detail = transaction as unknown as IPaymentSession;
			const channel = detail.req?.merchant?.channels?.find(entry => entry.linked?.id === detail.channel_id);
			if (detail.processing_id && channel?.integration?.common?.type === "decta") item.processingId = identifier(detail.processing_id);
			else {
				item.processing = item.processingLog = "skipped";
				item.reason = !detail.processing_id ? "No processing ID." : "Decta integration was not identified in transaction channel details.";
			}
		}
		report("transactions", rows.length, rows.length, msg("transactionDetailsCollected"));
		for (let index = 0; index < manifest.items.length; index++) {
			const item = manifest.items[index];
			report("processing", index, rows.length, msg("templateDectaDetailsTransactionOf_4ba4bd24", { v0: item.id, v1: index + 1, v2: rows.length }));
			if (!item.processingId) continue;
			const processing = record(await read(`processing/session/${item.processingId}`, {}));
			const session = record(processing?.session);
			if (!session?.ssid) throw new Error(msg("templateTransactionDectaReturnedNoSessionId_6c403f14", { v0: item.id }));
			item.processingSessionId = identifier(session.ssid);
			await put(`decta/${item.id}.json`, processing);
			item.processing = "complete";
		}
		report("processing", rows.length, rows.length, msg("dectaDetailsCollected"));
		for (let index = 0; index < manifest.items.length; index++) {
			const item = manifest.items[index];
			if (item.transaction === "skipped") continue;
			report("logs", index, rows.length, msg("templateTransactionLogOf_b3bc26e6", { v0: item.id, v1: index + 1, v2: rows.length }));
			const log = await read("session/log", { id: rows[index].id });
			if (!Array.isArray(log)) throw new Error(msg("templateTransactionInvalidTransactionLogResponse_9da5f81a", { v0: item.id }));
			await put(`logs/transactions/${item.id}.json`, log);
			item.log = "complete";
			if (!item.processingSessionId) continue;
			const entries: unknown[] = [];
			currentLogPages = 0;
			currentLogTotalPages = null;
			const seenPages = new Set<string>();
			let logPages: number | null = null;
			let logTotal: number | null = null;
			for (let page = 0; ; page++) {
				if (page >= MAX_PAGES) throw new Error(msg("templateTransactionProcessingLogPaginationExceededTheSafetyLimit_6fce22e1", { v0: item.id }));
				report("logs", index, rows.length, msg("templateDectaLogTransactionPage_3b6bb388", { v0: item.id, v1: page + 1 }));
				const result = pageData(await read("processing/log/get", { payment_id: item.processingSessionId, page, limit: 20 }));
				logPageRequests++; currentLogPages++;
				currentLogTotalPages = result.totalPages ?? (result.total !== null ? Math.ceil(result.total / 20) : null);
				if (result.page !== null && result.page !== page) throw new Error(msg("unexpectedProcessingLogPage"));
				if (page === 0) { logPages = result.totalPages; logTotal = result.total; }
				else if ((logPages !== null && result.totalPages !== null && logPages !== result.totalPages)
					|| (logTotal !== null && result.total !== null && logTotal !== result.total)) {
					throw new Error(msg("processingLogsChangedDuringCollectionThisLogIsIncomplete"));
				}
				if (result.items.length) {
					const signature = JSON.stringify(result.items);
					if (seenPages.has(signature)) throw new Error(msg("theServerRepeatedAProcessingLogPage"));
					seenPages.add(signature);
					await put(`logs/decta-pages/${item.id}/${String(page).padStart(5, "0")}.json`, result.items);
					entries.push(...result.items);
				}
				const finished = logPages !== null ? page + 1 >= logPages
					: logTotal !== null ? entries.length >= logTotal : result.items.length === 0;
				if (finished) {
					if (logTotal !== null && entries.length !== logTotal) throw new Error(msg("processingLogsAreIncomplete"));
					await put(`logs/decta/${item.id}.json`, entries);
					item.processingLog = "complete"; completedDectaLogs++; currentLogPages = 0; break;
				}
				if (!result.items.length) throw new Error(msg("aProcessingLogPageIsMissing"));
			}
		}
		report("logs", rows.length, rows.length, msg("allAvailableLogsCollected"));
	} catch (error) {
		manifest.status = signal.aborted ? "cancelled" : "failed";
		manifest.error = signal.aborted ? "Collection stopped. Pending items were not downloaded." : message(error);
		manifest.stoppedAt = { stage: progress.stage, message: progress.message };
	} finally {
		running = false;
		manifest.finishedAt = new Date().toISOString();
		manifest.listed = rows.length;
	}
	// Small metadata is kept even when a request failed or the memory guard stopped collection.
	const includedRows = rows.filter((_, index) => !manifest.items[index].excluded);
	files["transactions.json"] = encoder.encode(JSON.stringify(includedRows, null, 2));
	files["transactions.csv"] = encoder.encode(batchRowsCsv(includedRows));
	files["excluded.json"] = encoder.encode(JSON.stringify(manifest.items.filter(item => item.excluded).map(item => ({ id: item.id, ...item.excluded })), null, 2));
	const finalPart = partInfo(true);
	manifest.parts.push({ number: finalPart.number, files: finalPart.files, final: true });
	files[`parts/part-${String(finalPart.number).padStart(3, "0")}.json`] = encoder.encode(JSON.stringify(finalPart, null, 2));
	files["manifest.json"] = encoder.encode(JSON.stringify(manifest, null, 2));
	files["README.txt"] = encoder.encode(
		msg("batchExportReadme", { exportId: manifest.exportId, parts: manifest.parts.length, instructions: msg("batchExportPartInstructions"), maxTransactions, interval: BATCH_REQUEST_INTERVAL_MS }),
	);
	return { files, manifest };
}
